import { createFhirDate } from '@quromedical/fhir-common';
import {
  createBaseCareTeam,
  CareTeamValidationSchema,
  CareTeamMembersSchema,
} from '@quromedical/models';
import { useNavigation } from '@react-navigation/native';
import { Row } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { OrganizationFetcher } from 'core/group';
import { Alert, Skeleton, SkeletonProvider, Snackbar } from 'design-system';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import { useList } from 'hooks/useList';
import { useRevalidation } from 'hooks/useRevalidation';
import { CareTeamApi } from 'integration/aggregate';
import React, { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';

import {
  ManagedOutsideOfForm,
  areItemsValid,
  createEmptyItem,
  getInitialCareTeamLines,
} from './CareTeamForm';
import { createFields } from './fields';
import { GeneralForm } from './form';

export interface CreateCareTeamBaseTemplate extends createBaseCareTeam {
  teamMembers: CareTeamMembersSchema[];
}
export interface CreateCareTeamTemplate extends Omit<CreateCareTeamBaseTemplate, 'teamMembers'> {
  teamMembers: ManagedOutsideOfForm;
}
interface CreateFormProps {
  organizationFetcher: OrganizationFetcher;
  onCancel?: () => void;
}
type State = 'editing' | 'submitting' | 'item-error' | 'submission-error';

export const CareTeamCreateCard: React.FC<CreateFormProps> = ({
  organizationFetcher,
  onCancel,
}) => {
  const navigation = useNavigation();
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const revalidate = useRevalidation('CareTeam');

  const initialValues = {};

  const fieldData = createFields(organizationFetcher);

  const state = useFiniteState<State>('editing');

  const initialLines = useMemo(() => getInitialCareTeamLines(), []);
  const lines = useList(initialLines);

  const items = lines.items.map(i => i.value);
  const itemsValid = areItemsValid(items);

  const onSubmit = useCallback<SubmissionHandler<CreateCareTeamTemplate>>(
    async (formData: CreateCareTeamTemplate) => {
      if (!itemsValid) {
        state.set('item-error');
        return {};
      }
      setLoading(true);
      const teamMembers = items.map(t => {
        return {
          role: [t.data.role],
          member: t.data.member,
        };
      });

      try {
        const api = new CareTeamApi();

        const result = await api.createCareTeam({
          status: formData.status,
          name: formData.name,
          period: {
            start: createFhirDate(new Date()),
          },
          managingOrganization: [formData.managingOrganization],
          teamMembers,
        });

        navigation.navigate('Admin', {
          screen: 'CareTeam',
          params: {
            screen: 'View',
            params: {
              id: result.id || '',
            },
          },
        });

        await revalidate();
        setLoading(false);
        return {};
      } catch (error) {
        logger.error(error);
        setHasError(true);
        return { error };
      }
    },
    [items, itemsValid, navigation, revalidate, state]
  );

  const onAlertClose = useCallback(() => {
    setHasError(false);
  }, []);

  const isError = state.is('submission-error') || state.is('item-error');
  const errorMessage = state.is('submission-error')
    ? strings.ClaimTemplateFormSubmissionError
    : strings.ClaimTemplateFormItemsError;

  const error = isError ? errorMessage : undefined;

  return (
    <SkeletonProvider loading={loading}>
      <Row alignItems="center" justifyContent="space-between" unsetZIndex>
        <Skeleton>
          <CrudForm<CreateCareTeamTemplate>
            fields={fieldData}
            initialValues={{ ...initialValues, teamMembers: undefined }}
            validationSchema={CareTeamValidationSchema}
            error={error}
            showErrors={hasError}
            onSubmit={onSubmit}
            secondaryButtonText={strings.ButtonTextCancel}
            onSecondarySubmit={onCancel}
            showSecondarySubmitButton={!!onCancel}
            includeHeading={true}
          >
            {lines.items.map((item, index) => {
              const isLast = index === lines.items.length - 1;
              const canRemove = lines.items.length > 1;

              return (
                <GeneralForm
                  key={item.key}
                  organizationFetcher={organizationFetcher}
                  onChange={(data, isValid) => {
                    state.set('editing');

                    lines.update(item.key, { ...item.value, data, isValid });
                  }}
                  onRemove={canRemove ? () => lines.remove(item.key) : undefined}
                  onAdd={
                    isLast
                      ? () => {
                          const emptyItem = createEmptyItem();
                          lines.update(item.key, { ...item.value, hasSubmitted: true });
                          lines.add(emptyItem.key, emptyItem.value);
                        }
                      : undefined
                  }
                  data={item.value.data}
                  showErrors={item.value.hasSubmitted || hasError}
                />
              );
            })}
          </CrudForm>
        </Skeleton>
        <Snackbar isOpen={hasError} onClose={onAlertClose}>
          <Alert
            actionIcon="close"
            backgroundColor="status-critical"
            title={strings.AlertError}
            onAction={onAlertClose}
            body={strings.CareTeamCreateError}
          />
        </Snackbar>
      </Row>
    </SkeletonProvider>
  );
};
